import { tt } from '@agos/shared'

/**
 * What the dispatch board may do to an order, and to whom it may give it.
 *
 * This is a *different* state machine from the archive's, on purpose. The board
 * moves orders forward through the front of the process and hands them to
 * drivers; it does not close them. `delivered` is never offered here — a driver
 * marks that from the app, at the door — and `ready` comes from the kitchen, not
 * from a dispatcher's click.
 *
 * Ported from legacy `pages/dispatch.tsx`.
 */

/**
 * `preparing` offers only a cancellation because moving it on is the kitchen's
 * call, and `on_the_way` / `at_address` can go *back* to `quality_check` because
 * a driver who could not deliver brings the food to the pass.
 */
export const NEXT_STATUSES: Record<string, string[]> = {
  pending: ['new', 'cancelled'],
  new: ['preparing', 'cancelled'],
  preparing: ['cancelled'],
  ready: ['quality_check', 'cancelled'],
  quality_check: ['cancelled'],
  on_the_way: ['cancelled'],
  at_address: ['cancelled'],
}

const NEXT_LABELS: Record<string, string> = {
  new: tt('✓ Prijať'),
  preparing: tt('→ V príprave'),
  quality_check: tt('→ Kontr. kvality'),
  on_the_way: tt('→ Na ceste'),
  at_address: tt('→ Na adrese'),
  delivered: tt('→ Doručená'),
  cancelled: tt('Stornovať'),
}

/**
 * The same destination reads differently depending on where it is coming from.
 * `→ Kontr. kvality` is progress; from a driver who turned back it is a return,
 * and the arrow has to point the other way or a dispatcher reads it as forward
 * motion.
 */
export function nextStatusLabel(current: string | undefined, next: string): string {
  if ((current === 'on_the_way' || current === 'at_address') && next === 'quality_check') {
    return tt('← Vrátiť na kont. kvality')
  }
  return NEXT_LABELS[next] ?? next
}

export function nextStatusesFor(status: string | undefined): string[] {
  return NEXT_STATUSES[status ?? ''] ?? []
}

/**
 * The close a bar bill gets and a delivery does not.
 *
 * `NEXT_STATUSES` has no `delivered` anywhere, deliberately: a delivery is closed by the driver from
 * their own screen, which is what makes §14.5's wallet credit land on the right journey. A bar bill has
 * no journey and no driver — legacy closes it from the board, on `ready` or `quality_check`, and the
 * port had dropped that action along with the waiter question that goes with it (FB-075).
 */
export function mayCloseFromBoard(
  order: { type?: string; status?: string },
  channels?: readonly { slug?: string; onPremise?: boolean }[],
): boolean {
  // §9.391 - the channel, not the two names. `na_sekeru` sits on this board in `quality_check` and
  // was offered no close at all, which is the screen half of the owner's *"we cant move it"*.
  const known = channels?.find((channel) => channel.slug === order.type)
  const inHouse =
    known !== undefined && known.onPremise !== undefined
      ? known.onPremise
      : order.type === 'bar' || order.type === 'restaurant'
  return inHouse && (order.status === 'ready' || order.status === 'quality_check')
}

/** Everything the board shows. `delivered` and `cancelled` have left it. */
export const ACTIVE_STATUSES = [
  'pending',
  'new',
  'preparing',
  'ready',
  'quality_check',
  'on_the_way',
  'at_address',
]

export function isOnTheBoard(status: string | undefined): boolean {
  return ACTIVE_STATUSES.includes(status ?? '')
}

/**
 * Once a driver has left, the board cannot take the order off them.
 *
 * Re-assigning a delivery that is already on the road would move the wallet
 * credit for a journey somebody is physically making (BACKEND.md §14.5). The
 * driver hands it back from the app; the dispatcher cannot do it for them.
 */
export const DRIVER_REASSIGN_LOCKED = ['on_the_way', 'at_address', 'delivered']

export function canReassignDriver(order: {
  driverId?: number | null
  status?: string
}): boolean {
  if (!order.driverId) return true
  return !DRIVER_REASSIGN_LOCKED.includes(order.status ?? '')
}

/**
 * The statuses at which an order can actually be handed to somebody.
 *
 * <p>Legacy uses `ready` alone. `quality_check` is here as well because {@link isDispatchable} is
 * `quality_check && driverId`: gating on `ready` would leave an order that had reached quality check
 * with no way to get a driver and therefore no way to go out at all.
 */
const DRIVER_ASSIGNABLE = ['ready', 'quality_check']

/**
 * What the card should draw where the driver picker is - the owner's item B, §9.309, FB-483.
 *
 * <p><b>The owner said the picker on every order felt redundant, and it was.</b> Legacy renders it
 * only when `canChangeStatus && isDelivery && status === "ready"`; the port kept none of those and
 * drew the `<select>` unconditionally, varying only `disabled`. So a `preparing` order offered a
 * driver before the kitchen had finished, and an order already on the road showed a dead control.
 *
 * <p><b>`name` is what makes the narrowing safe.</b> A driver already assigned is always shown,
 * whatever the status, because hiding a control must never hide <i>who has the food</i>. An order sent
 * back from the road to `quality_check` names its driver and offers the picker again, because it is
 * assignable again.
 *
 * <p><b>Not gated on there being any driver on duty</b>, which is legacy's fourth condition and a
 * mistake to port: `GET /employees/drivers` answers only drivers seen in the last four hours, so an
 * empty list is ordinary. A picker that disappeared then would read as <i>this order needs no
 * driver</i>. An empty picker says the true thing.
 *
 * <p><b>`driven` is the channel's own `usesDrivers` capability, and getting there took two wrong
 * answers.</b> `belongsOnRoad` is <i>belongs on this board</i>, which a counter collection passes on
 * purpose. `!isCounterCollection` was the second guess and would have been wrong on the owner's data:
 * <b>`bar` has `usesDrivers: true` and 137 of its 1 082 orders have a driver</b> — FB-181 measured
 * that — so a counter channel here is driven one time in eight, and hiding the picker would have
 * broken a real workflow to tidy a screen.
 *
 * <p>`sales-channels.ts` states the rule that settles it: <i>"Ask what a channel does, never what it
 * is called."</i> So the only orders that lose the picker outright are those on a channel flagged as
 * not using drivers, and — much more commonly — every order the kitchen has not finished.
 */
/**
 * §9.323, the owner's A12 - four answers, not three.
 *
 * `'reassign'` is *somebody is on this and it could still be changed*: the card shows the driver's
 * name with a small way to reveal the picker. It exists because `'assign'` used to cover both
 * *nobody is on this* and *somebody is*, so an order already on its way carried a dropdown over the
 * driver's name - the widest control on the card, on a board that is a wall of cards during a rush.
 */
export type DriverControl = 'none' | 'assign' | 'name' | 'reassign'

export function driverControlFor(
  order: { status?: string; type?: string; driverId?: number | null },
  options: { driven: boolean; mayChange: boolean },
): DriverControl {
  // Whoever has it, said out loud, before any other consideration. This branch is why the rest can
  // be as strict as it is.
  const assigned = Boolean(order.driverId)
  if (!options.driven) return assigned ? 'name' : 'none'
  if (!options.mayChange) return assigned ? 'name' : 'none'
  if (!canReassignDriver(order)) return 'name'
  if (DRIVER_ASSIGNABLE.includes(order.status ?? '')) {
    // §9.323 - the owner's A12: *"Dropdown only on unassigned orders."* An assigned one is still
    // changeable, so it is `'reassign'` and not `'name'`; what changes is that the picker has to be
    // asked for.
    return assigned ? 'reassign' : 'assign'
  }
  return assigned ? 'name' : 'none'
}

export interface DispatchableOrder {
  id?: number
  status?: string
  driverId?: number | null
}

/**
 * An order may be sent on its way only when the kitchen has finished it *and* a
 * driver is on it. Both halves matter: dispatching without a driver loses track
 * of who has the food, and dispatching before `quality_check` sends out a bag
 * nobody checked.
 */
export function isDispatchable(order: DispatchableOrder): boolean {
  return order.status === 'quality_check' && Boolean(order.driverId)
}

export interface DispatchRun {
  driverId: number
  orderIds: number[]
}

/**
 * Groups the selection into one run per driver.
 *
 * A run is the unit the backend understands — a driver leaving with three bags
 * is one journey, not three — so grouping here is what makes the mileage and the
 * wallet credit come out right.
 */
export function dispatchRuns(orders: DispatchableOrder[]): DispatchRun[] {
  const byDriver = new Map<number, number[]>()

  for (const order of orders) {
    if (!isDispatchable(order)) continue
    const driverId = Number(order.driverId)
    const existing = byDriver.get(driverId)
    if (existing) existing.push(order.id ?? 0)
    else byDriver.set(driverId, [order.id ?? 0])
  }

  return [...byDriver.entries()].map(([driverId, orderIds]) => ({ driverId, orderIds }))
}

/**
 * Whether the board may still open an order for editing — §9.124, the owner's point 10.
 *
 * <p><b>Not the same question as "is the order open".</b> Legacy's rule is `isActive && !(isDelivery
 * && status is on_the_way or at_address)`: once the driver has the bag in the car, the order on the
 * screen is no longer the order in the world. Editing it then changes a total the customer was
 * already quoted and a receipt that may already be printed, and the driver never sees any of it.
 *
 * <p>A collected or cancelled order is likewise finished — there is nothing left to change about a
 * sale that has happened.
 */
export function mayEditFromBoard(order: { status?: string; type?: string }): boolean {
  const status = order.status ?? ''
  if (FINISHED_STATUSES.has(status)) return false
  const onTheRoad = status === 'on_the_way' || status === 'at_address'
  return !(order.type === 'delivery' && onTheRoad)
}

const FINISHED_STATUSES = new Set(['delivered', 'picked_up', 'cancelled'])

/** How an order arrived — §9.125. Blue for the web, orange for a phone call, nothing for neither. */
export function sourceBadge(order: { source?: string }): { label: string; tone: string } | null {
  if (order.source === 'web') {
    return { label: tt('Web'), tone: 'bg-blue-500/20 text-blue-400 border-blue-500/35' }
  }
  if (order.source === 'phone') {
    return { label: tt('Telefón'), tone: 'bg-orange-500/20 text-orange-400 border-orange-500/35' }
  }
  return null
}

/**
 * Whether an online order has actually been paid — §9.125, re-gated in §9.154.
 *
 * <p>Only ever shown for an order that went through online payment at all. A cash delivery has no
 * payment status worth a badge, and drawing *čaká* against one would say a payment is outstanding
 * that was never expected.
 *
 * <p><b>The gate is the method, not the status</b>, which is legacy's rule and was ours only by
 * accident. Asking `paymentStatus` meant a CardPay order the customer had not paid yet — method
 * `card_online`, status still null — drew no badge at all, and no badge reads as *nothing
 * outstanding*. That is the one case the badge exists for: a courier setting off to an unpaid
 * CardPay order. Anything that is not `online_paid` is *čaká*, exactly as legacy has it.
 */
export function paymentBadge(
  order: { paymentStatus?: string; paymentMethod?: string },
): { label: string; tone: string } | null {
  const online = order.paymentMethod === 'card_online' || order.paymentStatus === 'online_pending'
  if (!online && order.paymentStatus !== 'online_paid') return null
  if (order.paymentStatus === 'online_paid') {
    return {
      label: tt('✓ Platba schválená'),
      tone: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/35',
    }
  }
  return {
    label: tt('⏳ Platba čaká'),
    // §9.373, FB-667 - **red, because legacy is red** (`dispatch.tsx:474`) and the owner asked for
    // it by colour: *"if its platba online, and its waiting, it should be red, if it prešlo, green"*.
    // Amber reads as *in progress*; an unpaid order a driver is about to set off with is a problem,
    // not a stage.
    tone: 'bg-red-500/20 text-red-400 border-red-500/35',
  }
}

/**
 * Whether a contact has ordered before — §9.125, and the reason the owner asks for it by name.
 *
 * <p><b>From the second order, not the first.</b> Legacy's rule is `count > 1`. A first-time caller
 * is not "1× verified", they are unverified; a green `1×` would say the opposite of the truth on
 * exactly the order that deserves a second look before a driver is sent out.
 */
export function verificationOf(count: number | undefined): { label: string; known: boolean } {
  if (count !== undefined && count > 1) return { label: `${count}×`, known: true }
  return { label: tt('Neoverené'), known: false }
}

// ─── §9.154 — what legacy's row says and ours did not ────────────────────────
//
// All six are pure functions taking `now` as an argument rather than reading the clock, because a
// dispatch board is *about* the clock: a helper that calls `Date.now()` itself can only be tested at
// the moment the test runs, which is exactly the trap `agos-the-customer-app-shuts-at-night` records.

/**
 * How long an order has been waiting — legacy's `elapsed()`, formula for formula.
 *
 * <p>Seconds under a minute, whole minutes under an hour, `Nh Mm` above. **It does not floor at
 * zero**, and that is copied rather than corrected: an order stamped in the future reads as a
 * negative count of seconds in legacy too. Real data cannot produce it — an order is created when it
 * is created — but `dev-restore.sh --shift-to-today` can, and a guard here would make the §12 pair
 * differ for a case that does not exist in production.
 */
export function elapsedLabel(createdAt: string | undefined, now: number): string | null {
  if (createdAt === undefined || createdAt === '') return null
  const stamped = new Date(createdAt).getTime()
  if (Number.isNaN(stamped)) return null
  const seconds = Math.floor((now - stamped) / 1000)
  if (seconds < 60) return `${seconds}s`
  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) return `${minutes} min`
  return `${Math.floor(minutes / 60)}h ${minutes % 60}m`
}

/** Legacy's `createdAtLabel`: `Dnes HH:MM` on the day itself, `D. M. HH:MM` before that. */
export function arrivalLabel(createdAt: string | undefined, now: number): string | null {
  if (createdAt === undefined || createdAt === '') return null
  const stamped = new Date(createdAt)
  if (Number.isNaN(stamped.getTime())) return null
  const time = `${String(stamped.getHours()).padStart(2, '0')}:${String(stamped.getMinutes()).padStart(2, '0')}`
  const today = new Date(now)
  const sameDay =
    stamped.getFullYear() === today.getFullYear() &&
    stamped.getMonth() === today.getMonth() &&
    stamped.getDate() === today.getDate()
  return sameDay ? `${tt('Dnes')} ${time}` : `${stamped.getDate()}. ${stamped.getMonth() + 1}. ${time}`
}

/**
 * Which channel the order came through, named — legacy's `TYPE_LABELS`/`TYPE_COLORS`.
 *
 * <p>The board drew the icon and not the word. A truck and a bag are the same silhouette at a
 * glance across a busy screen, and *Osobný odber* is the one a driver must not be assigned to.
 */
export function channelBadge(type: string | undefined): { label: string; tone: string } | null {
  if (type === undefined || type === '') return null
  const known: Record<string, { label: string; tone: string }> = {
    delivery: { label: tt('Rozvoz'), tone: 'bg-violet-500/20 text-violet-400 border-violet-500/35' },
    restaurant: { label: tt('Reštaurácia'), tone: 'bg-red-500/20 text-red-400 border-red-500/35' },
    bar: { label: tt('Osobný odber'), tone: 'bg-blue-500/20 text-blue-400 border-blue-500/35' },
  }
  return known[type] ?? { label: type, tone: 'bg-muted/60 text-muted-foreground border-transparent' }
}

/**
 * How it will be paid - legacy's `PAYMENT_LABELS`/`PAYMENT_COLORS`.
 *
 * **Moved to `@agos/shared` in §9.384** and re-exported here, which keeps every call site on this
 * module unchanged. The owner asked for the driver app's payment badge to *"match colors from Rozvoz
 * nástenka"*, and `manager-driving` cannot import `manager-delivery` - both read `@agos/shared`. Two
 * screens naming the same six methods in two vocabularies is how they drift
 * (`agos-one-reader-spelled-twice`); this is the version with the tones the board has always drawn.
 */
export { paymentMethodBadge, type PaymentIcon } from '@agos/shared'

/**
 * What the bill was before the discount — legacy draws `13,02 € −2,87 € = 10,15 €`.
 *
 * <p>`original` is `total + discount` because that is what legacy computes and what the wire
 * supports: `subtotal` excludes delivery, so it is not the number that was struck through. Returns
 * null when nothing was taken off, so an undiscounted row stays one figure.
 */
export function discountParts(
  order: { total?: number | string; discount?: number | string },
): { original: number; discount: number; final: number } | null {
  const final = Number(order.total ?? 0)
  const discount = Number(order.discount ?? 0)
  if (!Number.isFinite(final) || !Number.isFinite(discount) || discount <= 0) return null
  return { original: final + discount, discount, final }
}

/**
 * A pre-order's promised time and the countdown to it — legacy's `minutesUntil`/`formatCountdown`.
 *
 * <p>Three tones because three situations: overdue is red and says by how much, within the hour is
 * amber and urgent, further out is amber and calm.
 */
export function scheduledBadge(
  order: { isPreorder?: boolean; scheduledAt?: string },
  now: number,
): { promisedAt: string; countdown: string; state: 'late' | 'urgent' | 'ahead' } | null {
  if (order.isPreorder !== true) return null
  const promised = order.scheduledAt
  if (promised === undefined || promised === '') return null
  const when = new Date(promised)
  if (Number.isNaN(when.getTime())) return null

  const minutes = Math.floor((when.getTime() - now) / 60000)
  const time = `${String(when.getHours()).padStart(2, '0')}:${String(when.getMinutes()).padStart(2, '0')}`
  const today = new Date(now)
  const sameDay =
    when.getFullYear() === today.getFullYear() &&
    when.getMonth() === today.getMonth() &&
    when.getDate() === today.getDate()
  const promisedAt = sameDay ? `${tt('Dnes')} ${time}` : `${when.getDate()}. ${when.getMonth() + 1}. ${time}`

  if (minutes < 0) {
    return { promisedAt, countdown: `${tt('oneskorenie')} ${Math.abs(minutes)} min`, state: 'late' }
  }
  const countdown =
    minutes === 0
      ? tt('Teraz!')
      : minutes < 60
        ? `${tt('za')} ${minutes} min`
        : `${tt('za')} ${Math.floor(minutes / 60)}h ${minutes % 60}m`
  return { promisedAt, countdown: `(${countdown})`, state: minutes <= 60 ? 'urgent' : 'ahead' }
}
